import {
  Grid,
  Card,
  CardContent,
  Typography,
  CardActionArea,
  Box,
  IconButton,
  Stack,
  Autocomplete,
  TextField,
} from '@mui/material';
import {
  Inventory,
  Business,
  Settings,
  Group,
  Terminal,
  Extension,
  ChatBubbleOutline,
} from "@mui/icons-material"
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { getUserClients, UserRoles } from '@/api/user.service';
import ClientSelectModal from '@/components/ClientSelectModal';
import { getClients, type Client } from '@/api/client.service';
import { ClienteModulesGrid } from '@/components/panel/ClienteModulesGrid';
import { ChatDrawer } from '@/components/panel/chat/ChatDrawer';
import { alpha } from "@mui/material/styles";

function SectionTitle({ children, action }: { children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
      <Typography variant="overline" sx={{ color: 'grey.400', fontWeight: 700, letterSpacing: '0.08em' }}>
        {children}
      </Typography>
      {action}
    </Stack>
  );
}

type Module = {
  id: number;
  title: string;
  description: string;
  icon: any;
  path: string;
  roles?: UserRoles[];
};

const ALL_MODULES: Module[] = [
  {
    id: 1,
    title: 'Clientes',
    description: 'Configuração dos clientes usuários do App do Dono.',
    icon: Business,
    path: '/clientes',
    roles: [UserRoles.Admin],
  },
  {
    id: 2,
    title: 'Usuários',
    description: 'Gerencie permissões e perfis de membros.',
    icon: Group,
    path: '/usuarios',
  },
  {
    id: 3,
    title: 'Módulos',
    description: 'Veja quais usuários têm acesso a cada módulo e por qual empresa.',
    icon: Extension,
    path: '/modulos',
  },
  {
    id: 4,
    title: 'Agente de Conexão',
    description: 'Baixe e configure o agente em seu servidor.',
    icon: Terminal,
    path: '/agente-conexao',
  },
  {
    id: 5,
    title: 'Configurações',
    description: 'Veja e ajuste as preferências do sistema.',
    icon: Settings,
    path: '/configuracoes',
  },
  {
    id: 6,
    title: 'Logs',
    description: 'Consulta de logs de operações.',
    icon: Inventory,
    path: '/logs',
    roles: [UserRoles.Admin],
  },
];

const BASE_MODULES = ALL_MODULES.filter(module => !module.roles || module.roles.length == 0)

export const Dashboard = () => {
  const { user, setLoggedClient } = useAuth();
  const navigate = useNavigate();

  const [clientModalOpen, setClientModalOpen] = useState(false);
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [chatOpen, setChatOpen] = useState(false);
  const [adminClients, setAdminClients] = useState<Client[]>([]);
  const [selectedAdminClient, setSelectedAdminClient] = useState<Client | null>(null);
  const [splashFailed, setSplashFailed] = useState(false);

  // arte do tema claro do cliente logado — o dark é só para o app mobile
  const clientSplash = user?.loggedClient?.splash_url ?? null;

  // volta a tentar exibir quando o cliente (ou a arte) muda
  useEffect(() => setSplashFailed(false), [clientSplash]);

  const modules = useMemo(() => {
    if (!user) return BASE_MODULES;

    return ALL_MODULES.filter(
      module => !module.roles || module.roles.includes(user.role)
    );
  }, [user]);

  const isOperador = user?.role === UserRoles.Operador;
  const showClientModules = (user?.role === UserRoles.Cliente || isOperador) && !!user.loggedClient;
  const isAdmin = user?.role === UserRoles.Admin;

  useEffect(() => {
    if (!isAdmin) return;

    getClients({ offset: 0, limit: 200, active: true })
      .then(data => setAdminClients(data.clients ?? []))
      .catch(err => console.error("Failed to load clients:", err));
  }, [isAdmin]);

  useEffect(() => {
    if (!user || user.loggedClient || user.role === UserRoles.Admin) {
      return;
    }

    const initClients = async () => {
      try {
        const clients = await getUserClients(user.id);

        if (clients.length === 1 && clients[0]) {
          setLoggedClient(clients[0]);
        } else if (clients.length > 1) {
          setAvailableClients(clients);
          setClientModalOpen(true);
        }
      } catch (err) {
        console.error("Failed to load clients:", err);
      }
    };

    initClients();
  }, [user?.id, user?.loggedClient, user?.role]);

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* Header Section */}
      <Box
        sx={{
          mb: 6,
          p: 3,
          borderRadius: 4,
          background: (theme) => alpha(theme.palette.background.paper, 0.75),
          backdropFilter: 'blur(10px)',
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: (theme) => `0 4px 20px ${alpha(theme.palette.primary.main, 0.06)}`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          {clientSplash && !splashFailed && (
            <Box
              component="img"
              src={clientSplash}
              alt={user?.loggedClient?.name ?? 'Cliente'}
              onError={() => setSplashFailed(true)}
              sx={{
                width: 88,
                height: 88,
                borderRadius: 3,
                objectFit: 'cover',
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
                flexShrink: 0,
              }}
            />
          )}

          <Box>
            <Typography variant="h4" sx={{ fontWeight: 900, color: 'text.primary', letterSpacing: '-0.02em' }}>
              Olá, {user?.name?.split(' ')[0]}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary', mt: 0.5 }}>
              Aqui está o resumo da sua atividade hoje.
            </Typography>
          </Box>
        </Stack>

        <Box sx={{
          display: { xs: 'none', sm: 'block' },
          bgcolor: 'background.paper',
          px: 2, py: 1,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}>
          <Typography variant="caption" sx={{ fontWeight: 700, color: 'grey.400', textTransform: 'uppercase' }}>
            {new Date().toLocaleDateString('pt-BR', { weekday: 'long', day: 'numeric' })}
          </Typography>
        </Box>
      </Box>

      {showClientModules && (
        <Box sx={{ mb: 5 }}>
          <SectionTitle
            action={
              <IconButton
                size="small"
                onClick={() => setChatOpen(true)}
                title="Conversas"
                sx={{ bgcolor: "background.paper", border: "1px solid", borderColor: "divider" }}
              >
                <ChatBubbleOutline fontSize="small" />
              </IconButton>
            }
          >
            Módulos
          </SectionTitle>
          <ClienteModulesGrid userId={user!.id} clientId={user!.loggedClient!.id} />
          <ChatDrawer
            open={chatOpen}
            onClose={() => setChatOpen(false)}
            clientId={user!.loggedClient!.id}
            userId={user!.id}
          />
        </Box>
      )}

      {isAdmin && (
        <Box sx={{ mb: 5 }}>
          <SectionTitle
            action={
              <Autocomplete
                size="small"
                options={adminClients}
                value={selectedAdminClient}
                onChange={(_, client) => {
                  setSelectedAdminClient(client);
                  if (client) setLoggedClient(client);
                }}
                getOptionLabel={(client) => client.name}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                sx={{ width: 260, bgcolor: 'background.paper', borderRadius: 1 }}
                renderInput={(params) => <TextField {...params} placeholder="Selecionar empresa" />}
              />
            }
          >
            Módulos
          </SectionTitle>
          {selectedAdminClient ? (
            <ClienteModulesGrid clientId={selectedAdminClient.id} />
          ) : (
            <Typography color="text.secondary">
              Selecione uma empresa para ver os módulos habilitados.
            </Typography>
          )}
        </Box>
      )}

      {!isOperador && (
        <>
          <SectionTitle>Administração</SectionTitle>

          <Grid container spacing={3}>
            {modules.map((module) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={module.id}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    borderRadius: 4,
                    border: '1px solid',
                    borderColor: 'divider',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    width: '100%',

                    '&:hover': {
                      transform: 'translateY(-5px)',
                      boxShadow: (theme) => theme.shadows[4],
                      borderColor: 'primary.main',
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => navigate(module.path)}
                    sx={{
                      height: '100%',
                      p: 1,
                      '&:hover': {
                        bgcolor: (theme) => alpha(theme.palette.primary.main, 0.03),
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      {/* Icon Container with subtle background color */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 56,
                          height: 56,
                          borderRadius: 3,
                          mb: 3,
                          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                          color: 'primary.main',
                        }}
                      >
                        <module.icon style={{ width: 32, height: 32 }} />
                      </Box>

                      <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary', mb: 1 }}>
                        {module.title}
                      </Typography>

                      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                        {module.description}
                      </Typography>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}
      {clientModalOpen && (
        <ClientSelectModal
          open={clientModalOpen}
          clients={availableClients}
          onSelect={(client) => {
            setLoggedClient(client);
            setClientModalOpen(false);
          }}
        />)}
    </Box>
  );
};